import { createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { ClipboardText, Briefcase, Lightning, Kanban, CalendarCheck, CheckCircle, Receipt, Buildings, Users, ArrowRight } from "@phosphor-icons/react";
import { fmtMoney, fmtDate } from "./portalUtils";

/* Where the sub-client oversight UI links to. The main-client PORTAL deep-links into its filtered lists; the ADMIN app
   shows the identical layout under the main client's account, linking to admin record pages and the oversight tabs. */
export const PORTAL_LINKS = {
  sub: (id) => `/portal/sub-clients/${id}`,
  counter: { open_requests: (id) => `/portal/requests?client=${id}`, awaiting_approval: (id) => `/portal/quotes?client=${id}`, active_jobs: (id) => `/portal/jobs?client=${id}`,
             active_emergencies: (id) => `/portal/emergencies?client=${id}`, active_projects: (id) => `/portal/projects?client=${id}`, active_contracts: (id) => `/portal/contracts?client=${id}` },
  viewAll: { requests: (id) => `/portal/requests?client=${id}`, jobs: (id) => `/portal/jobs?client=${id}`, emergencies: (id) => `/portal/emergencies?client=${id}`, projects: (id) => `/portal/projects?client=${id}`,
             contracts: (id) => `/portal/contracts?client=${id}`, quotes: (id) => `/portal/quotes?client=${id}`, invoices: (id) => `/portal/invoices?client=${id}` },
  request: (id) => `/portal/requests/${id}`, job: (id) => `/portal/jobs/${id}`, emergency: (id) => `/portal/emergencies/${id}`, project: (id) => `/portal/projects/${id}`,
  contract: (id) => `/portal/contracts/${id}`, quote: () => "/portal/quotes", invoice: (id) => `/portal/invoices/${id}`, property: null, teamUser: null,
};
export const adminLinks = (mainId) => {
  const tab = (t) => (id) => `/clients/${mainId}/sub-clients/${id}?tab=${t}`;
  return {
    sub: (id) => `/clients/${mainId}/sub-clients/${id}`,
    counter: { open_requests: tab("requests"), awaiting_approval: tab("quotes"), active_jobs: tab("jobs"), active_emergencies: tab("emergencies"), active_projects: tab("projects"), active_contracts: tab("contracts") },
    viewAll: { requests: () => "/job-requests", jobs: () => "/jobs", emergencies: () => "/emergency-jobs", projects: () => "/projects", contracts: () => "/contracts", quotes: () => "/quotes", invoices: () => "/invoices" },
    request: (id) => `/job-requests/${id}`, job: (id) => `/jobs/${id}`, emergency: (id) => `/emergency-jobs/${id}`, project: (id) => `/projects/${id}`,
    contract: (id) => `/contracts/${id}`, quote: (id) => `/quotes/${id}`, invoice: (id) => `/invoices/${id}`, property: (id, subId) => `/clients/${subId}#properties`, teamUser: (subId) => `/clients/${subId}#portal`,
  };
};
const LinksContext = createContext(PORTAL_LINKS);
export const SubClientLinksProvider = ({ links, children }) => <LinksContext.Provider value={links}>{children}</LinksContext.Provider>;
export const useSubClientLinks = () => useContext(LinksContext);

/* Shared counters for a sub-client (hub cards, dashboard panel, detail header). */
export const SUB_COUNTERS = [
  { key: "open_requests", label: "Open requests", icon: ClipboardText, cls: "text-blue-500" },
  { key: "awaiting_approval", label: "Awaiting approval", icon: CheckCircle, cls: "text-purple-500", hot: true },
  { key: "active_jobs", label: "Active jobs", icon: Briefcase, cls: "text-emerald-500" },
  { key: "active_emergencies", label: "Live emergencies", icon: Lightning, cls: "text-red-500", hot: true },
  { key: "active_projects", label: "Live projects", icon: Kanban, cls: "text-violet-500" },
  { key: "active_contracts", label: "Contracts", icon: CalendarCheck, cls: "text-teal-500" },
];

export function CounterChip({ def, value, subId, compact = false }) {
  const links = useSubClientLinks();
  const Icon = def.icon;
  const zero = !value;
  const body = (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 ${compact ? "text-[10px]" : "text-[11px]"} ${zero ? "border-border/60 text-muted-foreground/70" : def.hot ? "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 text-amber-800 dark:text-amber-200 font-semibold" : "border-border bg-muted/40"}`}
      title={def.label} data-testid={`sub-counter-${def.key}`}>
      <Icon size={compact ? 10 : 12} className={zero ? "" : def.cls} weight="duotone" /> {value ?? 0}{!compact && <span className="hidden sm:inline text-muted-foreground font-normal">{def.label.toLowerCase()}</span>}
    </span>
  );
  return subId ? <Link to={links.counter[def.key](subId)} onClick={(e) => e.stopPropagation()} className="hover:opacity-80">{body}</Link> : body;
}

export function SubClientCard({ sub, footer }) {
  const links = useSubClientLinks();
  const c = sub.counts || {};
  return (
    <div className="border border-border rounded-lg bg-card hover:border-primary/40 card-lift group" data-testid={`sub-client-card-${sub.id}`}>
    <Link to={links.sub(sub.id)} className="block p-4" data-testid={`sub-client-card-link-${sub.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-md bg-sky-500/10 text-sky-700 dark:text-sky-300 flex items-center justify-center shrink-0 font-bold font-[Chivo]">{(sub.display_name || "?")[0]}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate group-hover:text-primary" data-testid="sub-client-name">{sub.display_name}</p>
              <p className="text-[11px] text-muted-foreground truncate">{[sub.contact_name, sub.email].filter(Boolean).join(" · ") || "No contact on file"}</p>
            </div>
          </div>
        </div>
        <div className="text-right shrink-0">
          {c.outstanding_amount > 0 ? (
            <p className={`text-sm font-semibold font-[Chivo] ${c.overdue_invoices ? "text-red-600" : "text-amber-600"}`} data-testid="sub-client-outstanding">{fmtMoney(c.outstanding_amount)}</p>
          ) : <p className="text-sm font-semibold font-[Chivo] text-emerald-600">£0.00</p>}
          <p className="text-[10px] text-muted-foreground">{c.overdue_invoices ? `${c.overdue_invoices} overdue` : "outstanding"}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {SUB_COUNTERS.map((d) => <CounterChip key={d.key} def={d} value={c[d.key]} subId={sub.id} />)}
      </div>
      <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-3">
          <span className="inline-flex items-center gap-1"><Buildings size={11} /> {c.properties ?? 0} propert{c.properties === 1 ? "y" : "ies"}</span>
          <span className="inline-flex items-center gap-1"><Users size={11} /> {c.portal_users ?? 0} portal user{c.portal_users === 1 ? "" : "s"}</span>
          {c.upcoming_visits > 0 && <span className="inline-flex items-center gap-1"><CalendarCheck size={11} /> {c.upcoming_visits} upcoming visit{c.upcoming_visits === 1 ? "" : "s"}</span>}
        </span>
        <span className="inline-flex items-center gap-1">{c.last_activity ? `Last activity ${fmtDate(c.last_activity)}` : "No activity yet"} <ArrowRight size={11} className="text-primary" /></span>
      </div>
    </Link>
    {footer && <div className="px-4 py-2.5 border-t border-border/60">{footer}</div>}
    </div>
  );
}

export function SubClientRow({ sub }) {
  const links = useSubClientLinks();
  const c = sub.counts || {};
  return (
    <li>
      <Link to={links.sub(sub.id)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors" data-testid={`dash-sub-client-${sub.id}`}>
        <div className="h-8 w-8 rounded-md bg-sky-500/10 text-sky-700 dark:text-sky-300 flex items-center justify-center shrink-0 text-xs font-bold font-[Chivo]">{(sub.display_name || "?")[0]}</div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold truncate">{sub.display_name}</p>
          <div className="mt-1 flex flex-wrap gap-1">
            {SUB_COUNTERS.filter((d) => ["open_requests", "active_jobs", "active_emergencies", "awaiting_approval"].includes(d.key)).map((d) => <CounterChip key={d.key} def={d} value={c[d.key]} compact />)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-xs font-semibold font-[Chivo] ${c.outstanding_amount > 0 ? "text-amber-600" : "text-muted-foreground"}`}>{fmtMoney(c.outstanding_amount)}</p>
          <p className="text-[10px] text-muted-foreground inline-flex items-center gap-0.5"><Receipt size={10} /> outstanding</p>
        </div>
      </Link>
    </li>
  );
}
